import type {ObservableMap} from "mobx";
import {makeAutoObservable, observable} from "mobx";
import type {CategorizationReviewPageController} from "./CategorizationReviewPageController";
import type {MithraMaterializedApi} from "../../../services/MithraMaterializedApi";
import {getAcceptAlertMessage, getEditAlertMessage} from "../components/review-table/selection/SelectionMessages.ts";
import type {MatReviewAggregatedGroupData} from "../../../services/classes/MaterializedClasses.ts";
import type {GridApi, GridCallbackDetails, GridRowSelectionModel} from "@mui/x-data-grid-premium";
import {
    countReviewGroupClosedForReview,
    countReviewGroupOpenForReview,
    countReviewRowNotAcceptable,
    isReviewRowAcceptable,
    isReviewRowOpenForReview,
} from "../../../services/classes/MatReviewHelpers.ts";
import type {MuiXAggregateResponse} from "../../../components/muix-data-grid/MuixApiTypes.ts";
import {CancellableAxoisRequestManager} from "../../../stores/managers/RequestManager.tsx";
import type {
    PartGridGroupModel,
    PartGridRowModel,
    Row,
} from "../components/review-table/review-table-muix/CategorizationTableDataModel.ts";
import {GROUP_VAL} from "../components/review-table/review-table-muix/CategorizationTableDataModel.ts";
import {CategorizationReviewApi} from "./CategorizationReviewApi.ts";

export class CategorizationReviewPageMuixSelectionController {
    readonly totalCountController: TotalCountController;

    /**
     * Contains all selected rows.
     * Including
     * - child and parents
     * - rows not visible in the current page
     */
    public selectedRows = new Map() as ObservableMap<string, Row>;

    /**
     * If everything is selected
     * including the items not visible in the current page
     *
     * This function is not using MuiX selection model
     * as it does not support selecting more than in view when using the server side model
     *
     * This is implemented as a special state, which might cause some strange behavior is the user manually selects
     * all rows in view. We can improve the behavior later if needed.
     * (maybe change `isAllSelected` > `isUserAllSelected`, and add `get isCurrentlyAllSelected`)
     */
    isAllSelected = false;

    get selectionIsLoading() {
        return this.totalCountController.muiDataGridAggregate.busy;
    }

    constructor(
        private categorizationReviewPageController: CategorizationReviewPageController,
        private materializedApi: MithraMaterializedApi,
    ) {
        makeAutoObservable(this, {
            // Prevent the GridRowModel to become observables for performance & architecture reasons
            selectedRows: observable.shallow,
        });
        this.totalCountController = new TotalCountController(this.materializedApi);
    }

    public onRowSelectionModelChange(rowSelectionModel: GridRowSelectionModel, details: GridCallbackDetails): void {
        const api = details.api as GridApi;
        // console.log('onRowSelectionModelChange', details.reason, rowSelectionModel, this.categorizationReviewPageController.gridApi === api);
        if (details.reason === 'singleRowSelection') {
            this.isAllSelected = false;
        }
        const selection = (Array.from(rowSelectionModel.ids) as string[]).map(id =>
            ([id, api.getRow(id) ?? this.selectedRows.get(id) ?? null] as [string, Row])
        );

        const missingRows = selection.filter(([, row]) => !row);
        if (missingRows.length > 0) {
            // Assume all rows that are selected should at some point have been in view so each Row should be known
            console.warn(`onRowSelectionModelChange: Could not find ${missingRows.length}/${selection.length} rows in selection controller! Clearing selection...`);
            this.clearSelection();
            return;
        }
        this.selectedRows.replace(selection);
    }

    /**
     * Returns the current selection, split into:
     * - singleSelectedRows: individual rows that are not part of a selected group (or there are no groups)
     * - groupSelectedRows: individual rows that are already part of a FULLY selected group
     * - fullSelectedGroups: groups that are fully selected
     * - allGroupValues: all unique group values in the selection (including partial groups as well)
     *
     * Note: Partial selected groups are not "selected" according to MuiX, so they are not part of the selection
     */
    get selection() {
        const selection = Array(...this.selectedRows.values());

        // noinspection TypeScriptValidateTypes,TypeScriptUnresolvedReference
        const fullSelectedGroups = selection.filter(row => row.group) as PartGridGroupModel[];

        const groups = new Set<string>(fullSelectedGroups.map(row => row.group));
        // Alternatively we can also make the groups by looking at row.id and GROUP_KEY...

        // Get the individual rows that are not part of a selected group
        // noinspection TypeScriptUnresolvedReference
        const rows = selection.filter(row => !row.group && !(row as PartGridRowModel).subGroupPagination) as PartGridRowModel[];
        const singleSelectedRows = rows.filter(row => !groups.has(row[GROUP_VAL]));
        const groupSelectedRows = rows.filter(row => groups.has(row[GROUP_VAL]));

        const allGroupValues = new Set<string>([...groups, ...singleSelectedRows.map(row => row[GROUP_VAL])]);

        return {
            singleSelectedRows,
            groupSelectedRows,
            fullSelectedGroups,
            allGroupValues,
        }
    }

    get hasSelection() {
        return this.isAllSelected || (this.selection.singleSelectedRows.length > 0) || (this.selection.fullSelectedGroups.length > 0);
    }

    get rootSelectionState(): {checked: boolean, indeterminate: boolean} {
        const checked = this.hasSelection;
        if (this.isAllSelected) {
            return {checked, indeterminate: false};
        }
        const nSelectedParts = this.numberOfPartsSelected;
        if (nSelectedParts === 0 || nSelectedParts === this.totalParts) {
            return {checked, indeterminate: false};
        }
        // TODO: Polishing: Check if all parts are selected from all selectable parts in view

        // otherwise we are indeterminate
        return {checked, indeterminate: true};
    }

    get numberOfPartsInSelection(): number {
        if (this.isAllSelected) {
            return this.totalParts;
        } else {
            const singleParts = this.selection.singleSelectedRows.length;
            const groupParts = this.selection.fullSelectedGroups
                .reduce((sum, g) => sum + g.count, 0);
            // console.log('numberOfPartsInSelection', singleParts, groupParts);
            return singleParts + groupParts;
        }
    }

    get numberOfPartsSelected(): number {
        if (this.isAllSelected) {
            const apiResp = this.totalCountController.muiDataGridAggregate.result?.aggregate;
            if (apiResp) {
                return apiResp.count - apiResp.n_pending_agg;
            }
            return this.totalCountController.initNumberOfPartsCount ?? 0; // Fallback
        } else {
            const singleParts = this.selection.singleSelectedRows
                .reduce((sum, row) => sum + (isReviewRowOpenForReview(row) ? 1 : 0), 0);
            const groupParts = this.selection.fullSelectedGroups
                .reduce((sum, g) => sum + countReviewGroupOpenForReview(g), 0);
            // console.log('numberOfPartsSelected', singleParts, groupParts);
            return singleParts + groupParts;
        }
    }

    get totalParts(): number {
        if (this.isAllSelected) {
            const apiResp = this.totalCountController.muiDataGridAggregate.result?.aggregate;
            if (apiResp) {
                // console.log('totalParts [2]', apiResp.count);
                return apiResp.count;
            }
        }
        // console.log('totalParts [1]', this.totalCountController.initNumberOfPartsCount ?? 0);
        return this.totalCountController.initNumberOfPartsCount ?? 0;
    }

    get numberOfPendingPartsInSelection(): number {
        if (this.isAllSelected) {
            const agg = this.totalCountController.muiDataGridAggregate.result?.aggregate;
            if (agg) {
                return agg.n_pending_agg;
            }
            return 0; // Fallback
        } else {
            const singleParts = this.selection.singleSelectedRows
                .reduce((sum, row) => sum + (!isReviewRowOpenForReview(row) ? 1 : 0), 0); // We can never select a pending part individually
            const groupParts = this.selection.fullSelectedGroups
                .reduce((sum, g) => sum + countReviewGroupClosedForReview(g), 0);
            // console.log('numberOfPendingPartsInSelection', singleParts, groupParts);
            return singleParts + groupParts;
        }
    }

    get numberOfNotAcceptablePartsInSelection(): number {
        if (this.isAllSelected) {
            const agg = this.totalCountController.muiDataGridAggregate.result?.aggregate;
            if (agg) {
                return agg.n_is_open_uncat_agg;
            }
        } else {
            const singleParts = this.selection.singleSelectedRows
                .reduce((sum, row) => sum + (!isReviewRowAcceptable(row) ? 1 : 0), 0);
            const groupParts = this.selection.fullSelectedGroups
                .reduce((sum, g) => sum + countReviewRowNotAcceptable(g), 0);
            // console.log('numberOfNotAcceptablePartsInSelection', singleParts, groupParts);
            return singleParts + groupParts;
        }
        return 0;
    }

    get numberOfSuppliersSelected(): number {
        if (this.isAllSelected) {
            return this.totalSuppliers;
        } else {
            return this.selection.allGroupValues.size;
        }
    }

    get totalSuppliers(): number {
        return this.totalCountController.initNumberOfSuppliersCount ?? 0;
    }

    get editCategoryAlertMessage() {
        return getEditAlertMessage(this.numberOfPartsInSelection, this.numberOfPendingPartsInSelection);
    }

    get acceptCategoryAlertMessage() {
        let n: number | null = null;
        let n1: number | null = null;
        let n2: number | null = null;
        if (this.isAllSelected) {
            const apiResp = this.totalCountController.muiDataGridAggregate.result?.aggregate;
            if (apiResp) {
                n = apiResp.count;
                n1 = apiResp.n_pending_agg;
                n2 = apiResp.n_is_open_uncat_agg;
            }
        } else {
            n = this.numberOfPartsInSelection;
            n1 = this.numberOfPendingPartsInSelection;
            n2 = this.numberOfNotAcceptablePartsInSelection;
        }
        return getAcceptAlertMessage(n, n1, n2);
    }

    onLoadGroups(nSuppliers: number) {
        this.totalCountController.initNumberOfPartsCount = null;
        this.totalCountController.initNumberOfSuppliersCount = nSuppliers;
        this.totalCountController.muiDataGridAggregate.cleanup()
    }

    onLoadSingles(nParts: number) {
        this.totalCountController.initNumberOfPartsCount = nParts;
        this.totalCountController.initNumberOfSuppliersCount = null;
        this.totalCountController.muiDataGridAggregate.cleanup()
    }

    init() {
        this.selectedRows.clear();
        this.isAllSelected = false;
        this.totalCountController.initNumberOfPartsCount = null;
        this.totalCountController.initNumberOfSuppliersCount = null;
    }

    clearSelection() {
        this.selectedRows.clear();
        this.isAllSelected = false;

        const gridApi = this.categorizationReviewPageController.reviewPageTableController.gridApi;
        gridApi?.selectRows([], true, true);
    }

    toggleSelectAll() {
        if (this.isAllSelected) {
            this.clearSelection();
        } else {
            const tableController = this.categorizationReviewPageController.reviewPageTableController;
            const filterController = this.categorizationReviewPageController.reviewPageFilterController;
            const relatedData = this.categorizationReviewPageController.relatedDataFieldsSpec;
            this.isAllSelected = true;

            const gridApi = tableController.gridApi;
            const ids = gridApi?.getAllRowIds() ?? [];
            gridApi?.selectRows(ids, true, false);

            const params = CategorizationReviewApi.getMuiXQuery(
                this.categorizationReviewPageController.categorizationStore.bagId,
                this.categorizationReviewPageController.reviewPageRevisionController.revision_id,
                {
                    filterModel: filterController.apiGridFilterModel,
                    groupFields: tableController.rowGroupingModel,
                    aggregationModel: tableController.aggregationModel,
                    relatedData,
                },
            )
            this.totalCountController.muiDataGridAggregate.request({params});
        }
    }

    onPageChange() {
        // onPageChange

        if (this.isAllSelected) {
            // When changing pages we remove the `isAllSelected`
            this.isAllSelected = false;

            // I haven't found a way to set the selection of all rows via `apiRef.selectRows` after a server update
            // const ids = api.getAllRowIds() ?? [];
            // api.selectRows(ids, true, false);
            // Alternatively: https://mui.com/x/react-data-grid/row-selection/#custom-checkbox-column
        }
    }

    getSelectedRowsInView(): PartGridRowModel[] {
        const gridApi = this.categorizationReviewPageController.reviewPageTableController.gridApi;
        // noinspection TypeScriptValidateTypes
        return [
            ...this.selection.singleSelectedRows.map(row => gridApi?.getRow(row.id || String(row.part))).filter(r => r),
            ...this.selection.groupSelectedRows.map(row => gridApi?.getRow(row.id || String(row.part))).filter(r => r)
        ]
    }

    getSelectedGroupsInView(): PartGridGroupModel[] {
        const gridApi = this.categorizationReviewPageController.reviewPageTableController.gridApi;
        // noinspection TypeScriptValidateTypes
        return this.selection.fullSelectedGroups.map(row => gridApi?.getRow(row.id || String(row.part))).filter(r => r)
    }
}

class TotalCountController {
    initNumberOfPartsCount: number | null = null;
    initNumberOfSuppliersCount: number | null = null;

    static readonly URL = '/m_review/base/muix/aggregate/';

    readonly muiDataGridAggregate: CancellableAxoisRequestManager<MuiXAggregateResponse<MatReviewAggregatedGroupData>>

    constructor(private materializedApi: MithraMaterializedApi) {
        makeAutoObservable(this);
        this.muiDataGridAggregate = new CancellableAxoisRequestManager(this.materializedApi.http, TotalCountController.URL)
    }
}
